import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useFormik } from 'formik';
import moment from 'moment';

// Material UI
import { Box, Container, Typography, Button } from '@mui/material';
import { Send } from '@mui/icons-material';

// Componentes
import PersonalInformationForm from '@components/Compromisos/PersonalInformationForm';
import ContactInformationForm from '@components/Compromisos/ContactInformationForm';
import AddressForm from '@components/Compromisos/AddressForm';
import UserForm from '@components/Compromisos/UserForm';
import SocialMediaForm from '@components/Compromisos/SocialMediaForm';
import AcademyForm from '@components/Compromisos/AcademyForm';
import VolunteerForm from '@components/Compromisos/VolunteerForm';
import SectionCard from '@components/Compromisos/SectionCard';
import CustomLoading from '@components/LinearProgress/CustomLoading';

// Data
import { VIGENCIA_INE } from '@data/constants';
import { CompromisoAddInterfacePublicRegister } from '@data/interfaces/CompromisoInterfaces';
import { CompromisoAddPublicSchema } from '@data/schemas/CompromisoSchemas';

// Utils
import { validaINE, handleAttrs, handleFormikSubmit } from '@utils/Utilities';
import { isEmptyOrNullObject, isNullOrUndefined } from '@utils/validations';
import { RECAPTCHA_SITE_KEY } from '@utils/global';
import { Swal } from '@utils/alerts';

// Servicios
import UserServices from '@services/UserServices';
import CatalogServices from '@services/CatalogServices';

// Assets
import avatar from '@assets/img/SianPan.png';

const Register = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const catalogsOptions = [
    { id: 'entidades' },
    { id: 'municipios' },
    { id: 'tipo_vialidad' },
    { id: 'generos' },
    { id: 'grados_estudios' },
    { id: 'ocupacion_laboral' },
    { id: 'voluntarios' },
  ];

  const [isLoading, setIsLoading] = useState({ state: true, message: 'Cargando datos...' });

  const formik = useFormik({
    initialValues: CompromisoAddInterfacePublicRegister,
    validationSchema: CompromisoAddPublicSchema,
    onSubmit: (values) => {
      onChangeCaptcha(handleAttrs(values));
    },
  });
  const [catalogs, setCatalogs] = useState(() => {
    let newObject = {};
    for (const item of catalogsOptions) {
      newObject[item.id] = [];
    }
    return newObject;
  });
  const [load, setLoad] = useState(true);
  const [dataUser, setDataUser] = useState({});

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    if (id) getUserData(id); // eslint-disable-next-line
  }, [id]);

  const getUserData = async (Username) => {
    if (!Username) return;

    try {
      setLoad(true);
      setIsLoading({ state: true, message: 'Cargando datos...' });

      const result = await UserServices.getDataUserOwner({ Username });
      const { results, response } = result;

      if (results) {
        const found = response?.ambito_perfil?.find((item) => item?.idTipo === 4) || 1;

        setDataUser({
          Nombre: response.Nombre,
          idUsuario: response.id,
          idPartido: response.idPartido,
          idEstructuraSocial: !isNullOrUndefined(found?.AmbitoValor) ? found?.AmbitoValor : 1,
        });

        await getCatalogs();
      } else throw new Error('El URL es invalido');
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setIsLoading((prev) => ({ ...prev, state: false }));
    }
  };

  const getCatalogs = async () => {
    try {
      const result = await CatalogServices.getCatalogsPublicEvents({ catalogs: catalogsOptions });
      const { results, response, message } = result;

      if (results) {
        setCatalogs(() => {
          let newObject = {};
          for (const item of catalogsOptions) {
            newObject[item.id] = response.catalogs[item.id];
          }
          return newObject;
        });
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setLoad(false);
    }
  };

  const onChangeCaptcha = useCallback(
    (values) => {
      if (!executeRecaptcha) return;

      executeRecaptcha('invito')
        .then((token) => {
          const data = { ...values, token: token };
          handleVerification(handleAttrs(data));
        })
        .catch((error) => Swal.fire({ title: error, icon: 'warning' }));
    }, // eslint-disable-next-line
    [executeRecaptcha]
  );

  const handleVerification = (values) => {
    if (!isEmptyOrNullObject(values)) {
      const res = validaINE(
        values.user.INE,
        values.user.Nombre,
        values.user.Paterno,
        values.user.Materno,
        values.user.idSexo,
        values.user.FechaNacimiento,
        values.user.idEstadoNacimiento
      );

      if (!res.results) {
        Swal.fire({
          title: `¡Atención!`,
          text: res.message,
          icon: 'info',
          reverseButtons: true,
          showCancelButton: false,
          confirmButtonText: 'Ok',
        });
      } else {
        if (values.user.VigenciaINE < VIGENCIA_INE) {
          Swal.fire({
            title: `¡Vigencia INE!`,
            text: 'Invite a la persona a renovar su credencial de elector, ya que no será válida como medio de identificación en el siguiente proceso',
            icon: 'info',
            reverseButtons: true,
            showCancelButton: false,
            confirmButtonText: 'Continuar',
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then((res) => {
            if (res.isConfirmed) handleSave(values);
          });
        } else handleSave(values);
      }
    }
  };

  const handleSave = async (values) => {
    setIsLoading({ state: true, message: 'Guardando datos...' });

    let data = {};

    Object.keys(CompromisoAddInterfacePublicRegister).forEach((key) => {
      data[key] = values[key] !== null ? values[key] : CompromisoAddInterfacePublicRegister[key];
    });

    data = {
      ...data,

      user: {
        ...data.user,
        INE: values.user.INE.toUpperCase(),
        VigenciaINE: values.user.VigenciaINE ? parseInt(values.user.VigenciaINE) : null,

        Nombre: values.user.Nombre.toUpperCase(),
        Paterno: values.user.Paterno.toUpperCase(),
        Materno: values.user.Materno.toUpperCase(),
        FechaNacimiento: moment(values.user.FechaNacimiento).format('YYYY-MM-DD'),
      },

      account: {
        ...data.account,
        Username: values.contact.Celular,
      },

      address: {
        ...data.address,
        CalleVive: `${values.address.TipoVialidad} ${values.address.CalleVive}`,
      },

      /* formation: {
        ...data.formation,
        initialDate: formatDateAPI(values.formation.initialDate),
        finalDate: formatDateAPI(values.formation.finalDate),
      }, */

      extras: {
        idPartido: dataUser.idPartido || 1,
        idUsuarioPropietario: dataUser.idUsuario || 1,
        idEstructuraSocial: dataUser.idEstructuraSocial || 1,

        medioConfirmacion: 'sms',
      },
    };

    delete data.address.TipoVialidad;
    delete data.account.Confirm;

    try {
      const result = await UserServices.createUser(data);
      const { success, results, response, message } = result;

      if (success && results) {
        const confirmSwal = (title) => {
          Swal.fire({
            backdrop: false,
            title: title,
            icon: 'info',
            confirmButtonText: 'Ingresar',
            html: `<b>Puede ingresar en</b> <a id="navigateToLogin" style="cursor:pointer;">SIAN</a>`,
            didOpen: () => {
              document.getElementById('navigateToLogin').addEventListener('click', () => {
                Swal.close();
                navigate('/login');
              });
            },
            preConfirm: () => navigate('/login'),
          });
        };

        if (response?.codigo) {
          Swal.fire({
            backdrop: false,
            title: message,
            icon: 'success',
            input: 'text',
            inputAttributes: { autocapitalize: 'off' },
            showCancelButton: false,
            confirmButtonText: 'Confirmar',
            showLoaderOnConfirm: true,
            preConfirm: async (code) => {
              if (code === response.codigo) {
                try {
                  const result_2 = await UserServices.codeValidate({ code: code, c: response.UUID });

                  if (result_2.success) confirmSwal('Usuario Validado');
                  else throw new Error('');
                } catch (e) {
                  Swal.showValidationMessage(`Error en la validación. Contacte al administrador.`);
                }
              } else Swal.showValidationMessage(`El codígo no coincide`);
            },
            allowOutsideClick: () => !Swal.isLoading(),
          });
        } else confirmSwal(message);

        clear();
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setIsLoading((prev) => ({ ...prev, state: false }));
    }
  };

  const clear = () => formik.resetForm();

  const handleSummit = () => handleFormikSubmit(formik);

  return (
    <>
      <CustomLoading isOpen={isLoading.state} message={isLoading.message} />

      {/* Hero */}
      <Box
        sx={{
          backgroundImage: `url(${require('@assets/img/back02.png')})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          py: { xs: 6, sm: 8 },
          textAlign: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,41,100,0.78)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <img
            alt="SIAN"
            src={avatar}
            style={{ width: 120, height: 120, objectFit: 'contain', marginBottom: 16 }}
          />
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
            Gracias por formar parte de nosotros
          </Typography>
          <Box
            sx={{
              display: 'inline-block',
              backgroundColor: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 5,
              px: 2.5,
              py: 0.5,
              mt: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Completa tu registro para unirte a Acción Nacional
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Cuerpo */}
      <Box sx={{ backgroundColor: '#eef2f7', py: { xs: 3, sm: 5 }, px: { xs: 1, sm: 2 } }}>
        <Container maxWidth="lg">

          <SectionCard number="1" icon="person" title="Datos de Usuario">
            <UserForm isLoading={load} formik={formik} showChip={false} />
          </SectionCard>

          <SectionCard number="2" icon="person" title="Datos personales">
            <PersonalInformationForm catalogs={catalogs} isLoading={load} formik={formik} notNacimiento showChip={false} />
          </SectionCard>

          <SectionCard number="3" icon="contact_phone" title="Contacto">
            <ContactInformationForm isLoading={load} formik={formik} showChip={false} />
          </SectionCard>

          <SectionCard number="4" icon="home" title="Domicilio donde vive">
            <AddressForm catalogs={catalogs} isLoading={load} formik={formik} showChip={false} />
          </SectionCard>

          <SectionCard number="5" icon="groups" title="Redes Sociales">
            <SocialMediaForm isLoading={load} formik={formik} showChip={false} />
          </SectionCard>

          <SectionCard number="6" icon="school" title="Formación Académica">
            <AcademyForm catalogs={catalogs} isLoading={load} formik={formik} showChip={false} />
          </SectionCard>

          {/* <SectionCard number="7" icon="accessibility" title="Participación en Acción Nacional">
            <VolunteerForm catalogs={catalogs} isLoading={load} formik={formik} showChip={false} />
          </SectionCard> */}

          {/* Botón guardar */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 4 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<Send />}
              onClick={handleSummit}
              sx={{
                px: 6,
                py: 1.5,
                borderRadius: '30px',
                fontSize: '1rem',
                fontWeight: 700,
                backgroundColor: '#0041a0',
                boxShadow: '0 4px 16px rgba(0,65,160,0.35)',
                textTransform: 'uppercase',
                letterSpacing: 1,
                '&:hover': {
                  backgroundColor: '#003490',
                  boxShadow: '0 6px 20px rgba(0,65,160,0.5)',
                },
              }}
            >
              Guardar registro
            </Button>
          </Box>

        </Container>
      </Box>
    </>
  );
};

const recaptcha = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <Register />
    </GoogleReCaptchaProvider>
  );
};

export default recaptcha;
